/**
 * localInfra.ts — queue #670 §R670-3·§R670-5.
 *
 * 가져다쓴 브로커 모듈 → opt-in gate → compose profile → 로컬 접속값 매핑 SSOT.
 * `LocalInfraSection` 렌더 데이터인 동시에 `localInfra.consistency.test.ts` 의 대조 대상 —
 * 값을 바꾸면 루트 `docker-compose.yml` 도 함께 갱신해야 한다(§R670-2 정합 결정).
 *
 * ★RabbitMQ vhost 예외 — take-this.json(C057) localhost 스니펫은 vhost `/` 이지만, 모듈
 * app 기본값(`RABBITMQ_VHOST:eraf`)과 정합하도록 이 SSOT 는 `eraf` 를 채택한다(§R670-1(b)
 * drift 해소 · §R670-2 compose 가 `RABBITMQ_DEFAULT_VHOST=eraf` 로 프로비저닝).
 * ★OTel 포트 4318 — config-properties.json 카탈로그 표기 기본(4317 gRPC)과 다르다. 로컬은
 * Boot 의 HTTP OTLP exporter(management.otlp.tracing.endpoint)를 따라 4318(HTTP)을 쓴다.
 * ★Keycloak 포트/realm — take-this.json(C035)의 issuer-uri 는 placeholder 도메인
 * (`https://idp.example.com/realms/eraf`)뿐이라 로컬 포트 리터럴이 없다. 8180 은 compose
 * 서비스 포트 매핑(앱 8080 과의 충돌 회피)에서 온 로컬 전용 결정값이며 realm `eraf` 는
 * `infra/brokers/setup-keycloak-eraf.sh` 산출물과 정합한다(§R670-2 설계 노트).
 */

export type LocalInfraProfile = 'kafka' | 'rabbitmq' | 'mongo' | 'elasticsearch' | 'jaeger' | 'keycloak';

export interface LocalInfraModule {
  /** 가져다쓴 공통 모듈 artifact 명 (take-this.json 키 기준 표시용) */
  module: string;
  /** opt-in gate property (기본 OFF) */
  gateKey: string;
  /** 루트 docker-compose.yml 서비스의 compose profile */
  profile: LocalInfraProfile;
  /** 로컬 기동 명령 */
  command: string;
  /** 로컬 접속 host (localhost 고정 — biz-ci 10.0.1.5 아님) */
  host: string;
  port: number;
  /** application.yml override 로 복사할 접속 라인들 (표의 "로컬 접속" 셀 원문) */
  connectionLines: string[];
  /** 부가 접속 속성(비교 검증용) — vhost/db/authSource/scheme/path/realm 등 */
  extra?: Record<string, string>;
}

export const LOCAL_INFRA_MODULES: LocalInfraModule[] = [
  {
    module: 'eraf-messaging-kafka',
    gateKey: 'eraf.messaging.kafka.enabled',
    profile: 'kafka',
    command: 'docker compose --profile kafka up -d',
    host: 'localhost',
    port: 9092,
    connectionLines: ['spring.kafka.bootstrap-servers: localhost:9092'],
  },
  {
    module: 'eraf-messaging-rabbitmq',
    gateKey: 'eraf.messaging.rabbitmq.enabled',
    profile: 'rabbitmq',
    command: 'docker compose --profile rabbitmq up -d',
    host: 'localhost',
    port: 5672,
    connectionLines: [
      'spring.rabbitmq.host: localhost',
      'spring.rabbitmq.port: 5672',
      'spring.rabbitmq.virtual-host: eraf',
      'spring.rabbitmq.username: eraf',
      'spring.rabbitmq.password: eraf_local',
    ],
    extra: { vhost: 'eraf' },
  },
  {
    module: 'eraf-data-mongo',
    gateKey: 'eraf.data.mongo.enabled',
    profile: 'mongo',
    command: 'docker compose --profile mongo up -d',
    host: 'localhost',
    port: 27017,
    connectionLines: [
      'spring.data.mongodb.host: localhost',
      'spring.data.mongodb.port: 27017',
      'spring.data.mongodb.database: eraf',
      'spring.data.mongodb.authentication-database: admin',
    ],
    extra: { db: 'eraf', authSource: 'admin' },
  },
  {
    module: 'eraf-data-elasticsearch',
    gateKey: 'eraf.data.elasticsearch.enabled',
    profile: 'elasticsearch',
    command: 'docker compose --profile elasticsearch up -d',
    host: 'localhost',
    port: 9200,
    connectionLines: ['spring.elasticsearch.uris: http://localhost:9200'],
    extra: { scheme: 'http' },
  },
  {
    module: 'eraf-actuator',
    gateKey: 'eraf.observability.otel.enabled',
    profile: 'jaeger',
    command: 'docker compose --profile jaeger up -d',
    host: 'localhost',
    port: 4318,
    connectionLines: ['management.otlp.tracing.endpoint: http://localhost:4318/v1/traces'],
    extra: { path: '/v1/traces' },
  },
  {
    module: 'eraf-security',
    gateKey: 'eraf.security.oauth2.enabled',
    profile: 'keycloak',
    command: 'docker compose --profile keycloak up -d',
    host: 'localhost',
    port: 8180,
    connectionLines: ['eraf.security.oauth2.resource.issuer-uri: http://localhost:8180/realms/eraf'],
    extra: { realm: 'eraf' },
  },
];

/** 6종 일괄 기동 — 우산 profile(§R670-2 `brokers`). */
export const LOCAL_INFRA_ALL_PROFILE = 'brokers';
export const LOCAL_INFRA_ALL_COMMAND = 'docker compose --profile brokers up -d';
